import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Maximize, Minimize, Pause } from "lucide-react";
import { api, fileUrl, slDate, BACKEND } from "@/lib/api";

const VARIANTS = {
  fade: { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } },
  slide: { initial: { opacity: 0, x: "8%" }, animate: { opacity: 1, x: 0 }, exit: { opacity: 0, x: "-8%" } },
  zoom: { initial: { opacity: 0, scale: 1.12 }, animate: { opacity: 1, scale: 1 }, exit: { opacity: 0, scale: 0.96 } },
};

function useFullscreen() {
  const [full, setFull] = useState(false);
  useEffect(() => {
    const h = () => setFull(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", h);
    return () => document.removeEventListener("fullscreenchange", h);
  }, []);
  const toggle = () => (document.fullscreenElement ? document.exitFullscreen() : document.documentElement.requestFullscreen?.());
  return [full, toggle];
}

function QrSlide({ data }) {
  return (
    <div className="absolute inset-0 grid place-items-center text-center px-6" data-testid="livewall-qr-slide">
      <div className="space-y-8">
        <p className="font-display text-5xl sm:text-7xl font-light">Imaš fotografijo?</p>
        <img src={`${BACKEND}/api/public/w/${data.slug}/qr.png`} alt="QR koda" className="w-64 h-64 sm:w-80 sm:h-80 mx-auto rounded-3xl bg-white p-3" />
        <p className="text-2xl text-white/80">Skeniraj in jo deli!</p>
      </div>
    </div>
  );
}

export default function LiveWall() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [slide, setSlide] = useState({ n: 0, idx: 0, qr: false });
  const [full, toggleFull] = useFullscreen();
  const seen = useRef(new Set());
  const queue = useRef([]);

  useEffect(() => {
    const load = async () => {
      try {
        const r = await api.get(`/wall/${token}`);
        const fresh = r.data.items.filter((m) => !seen.current.has(m.id));
        if (seen.current.size > 0) queue.current.push(...fresh.map((m) => m.id));
        fresh.forEach((m) => seen.current.add(m.id));
        setData(r.data);
        setErr(false);
      } catch (e) { setErr(true); }
    };
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [token]);

  useEffect(() => {
    let lock;
    navigator.wakeLock?.request("screen").then((l) => { lock = l; }).catch(() => {});
    return () => lock?.release?.();
  }, []);

  const items = data?.items || [];
  const cfg = data?.config || {};
  useEffect(() => {
    if (!items.length || cfg.paused) return;
    const t = setTimeout(() => {
      setSlide((s) => {
        const n = s.n + 1;
        if (cfg.qr_every > 0 && !s.qr && n % (cfg.qr_every + 1) === 0) return { n, idx: s.idx, qr: true };
        const nextId = queue.current.shift();
        const qi = nextId ? items.findIndex((m) => m.id === nextId) : -1;
        return { n, idx: qi >= 0 ? qi : (s.idx + 1) % items.length, qr: false };
      });
    }, (cfg.duration || 7) * 1000);
    return () => clearTimeout(t);
  }, [slide, items, cfg.paused, cfg.duration, cfg.qr_every]);

  if (err && !data) return <div className="fixed inset-0 bg-[#141213] text-white grid place-items-center text-center px-6" data-testid="livewall-unavailable"><div><p className="font-display text-4xl">Live Wall ni na voljo</p><p className="text-white/60 mt-3">Zaslon je izklopljen ali povezava ni več veljavna.</p></div></div>;
  if (!data) return <div className="fixed inset-0 bg-[#141213]" />;

  const cur = items[slide.idx % Math.max(items.length, 1)];
  const v = VARIANTS[cfg.transition] || VARIANTS.fade;
  return (
    <div className="fixed inset-0 text-white overflow-hidden" style={{ background: cfg.bg_color || "#141213" }} data-testid="livewall-page">
      <AnimatePresence mode="sync">
        {slide.qr ? (
          <motion.div key={`qr-${slide.n}`} {...v} transition={{ duration: 1.2 }} className="absolute inset-0"><QrSlide data={data} /></motion.div>
        ) : cur && (
          <motion.div key={`${cur.id}-${slide.n}`} {...v} transition={{ duration: 1.2 }} className="absolute inset-0">
            {cur.type === "video" ? (
              <video src={fileUrl(cur.url)} autoPlay muted playsInline className="absolute inset-0 w-full h-full object-contain" data-testid="livewall-video" />
            ) : (
              <>
                <img src={fileUrl(cur.url)} alt="" className="absolute inset-0 w-full h-full object-cover blur-2xl opacity-25 scale-110" />
                <img src={fileUrl(cur.url)} alt="" className="absolute inset-0 w-full h-full object-contain" data-testid="livewall-image" />
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
      {!items.length && (
        <div className="absolute inset-0 grid place-items-center text-center px-6" data-testid="livewall-empty">
          <div><p className="font-display text-5xl">{data.title}</p><p className="mt-4 text-white/60">Odobrene fotografije se bodo prikazale samodejno.</p></div>
        </div>
      )}
      <div className="absolute inset-x-0 bottom-0 p-6 sm:p-10 flex items-end justify-between bg-gradient-to-t from-black/60 to-transparent pointer-events-none">
        <div>
          {cfg.show_header && <><p className="font-display text-4xl sm:text-5xl font-light" data-testid="livewall-title">{data.title}</p><p className="text-white/60 text-sm tracking-[0.25em] uppercase mt-2">{slDate(data.wedding_date)}</p></>}
          {cfg.show_names && cur && !slide.qr && <p className="text-white/85 mt-4 text-base" data-testid="livewall-uploader">Fotografiral: {cur.uploader_name}</p>}
        </div>
        {!slide.qr && <img src={`${BACKEND}/api/public/w/${data.slug}/qr.png`} alt="QR koda" className="w-24 h-24 sm:w-32 sm:h-32 rounded-xl bg-white p-1.5" data-testid="livewall-qr" />}
      </div>
      {cfg.paused && <div className="absolute top-5 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full bg-black/50 text-sm flex items-center gap-2" data-testid="livewall-paused"><Pause size={14} /> Zaustavljeno</div>}
      <div className="absolute top-5 left-5 flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/70"><span className="w-2 h-2 rounded-full bg-[#C5A880] animate-pulse" /> V živo</div>
      <button onClick={toggleFull} className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 grid place-items-center" data-testid="livewall-fullscreen-toggle" aria-label="Celozaslonski način">
        {full ? <Minimize size={18} /> : <Maximize size={18} />}
      </button>
    </div>
  );
}
